import _ from 'lodash';
import tacHelpers from '../tacHelpers';
import geometry from '../../../scene/util/geometry';
import { isSection } from '../../../services/products';
import { isStandAlone } from '../../../services/products/models';

function leftOf(them, me) {
  return me.x + me.width > them.x;
}

function below(me, them) {
  return them.y >= me.y + me.height;
}

function behind(me, them) {
  return them.z >= me.z + me.depth;
}

function isInvisible(them) {
  return !them.width || !them.height || !them.depth;
}

function outsideWall(them) {
  return isStandAlone(them) && !tacHelpers.isWithinWall(them);
}

function intersects(itemOne, itemTwo) {
  const intersection = geometry.intersectingRect(itemOne, itemTwo);
  return intersection.height > 0 && intersection.width > 0;
}

function map2dot(map) {
  return `
  digraph {
    ${map
      .map(dep =>
        ` ${dep.item.itemid} [label=${dep.item.id}_${dep.item.type.replace(
          '-',
          '_'
        )}] -> {${dep.dependsOn.join(';')}};`.replace(' -> {}', '')
      )
      .join('\n')}
  }
  `;
}

function dependsOn(me, them) {
  if (outsideWall(me)) {
    // Always draw items outside the wall limits last
    return true;
  }

  /*
  Never depend if:
  1. We're behind, no matter where.
  2. We're left of something that is invisible
  3. They're outside the limits of the wall
  */
  if (
    behind(me, them) ||
    (isInvisible(them) && leftOf(me, them)) ||
    outsideWall(them)
  ) {
    return false;
  }

  if (intersects(me, them)) {
    //if both are sections and me has item(s), draw the smaller one first
    if (
      isSection(me) &&
      isSection(them) &&
      me.items?.length &&
      (geometry.contains(me, them) || geometry.contains(them, me))
    ) {
      return geometry.contains(me, them);
    }

    const allowedOverlap = tacHelpers.range.getAllowedOverlap(
      { items: [them, me] },
      them,
      me
    );

    // if we intersect, depend if they are behind or left of and is not contained by the other one
    return (
      behind(them, me) ||
      (them.x < me.x &&
        geometry.intersectingRect(them, me).width === allowedOverlap?.x &&
        !geometry.contains(them, me))
    );
  }

  // depend on stuff on the left, if we are not below it
  if (leftOf(them, me) && !below(me, them)) {
    return true;
  }

  // if we don't intersect, then depend on them if they're left and below
  return leftOf(them, me) && below(them, me);
}

function buildDependencies(items) {
  const dependencies = items.map(item => ({
    item: item,
    dependsOn: null,
  }));

  dependencies.forEach(function (dep) {
    dep.dependsOn = items
      .filter(item => item !== dep.item)
      .filter(item => dependsOn(dep.item, item))
      .map(item => item.itemid);
  });

  return dependencies;
}

function sortDependencies(nodes) {
  // This is an implementation of Kahn's algorithm for sorting a dependency graph
  // https://en.wikipedia.org/wiki/Topological_sorting#Kahn's_algorithm
  const out = [];
  const metDeps = nodes.filter(node => node.dependsOn.length === 0);
  const unmetDeps = nodes.filter(node => node.dependsOn.length !== 0);
  while (metDeps.length) {
    var done = metDeps.pop();
    out.push(done.item.itemid);
    //eslint-disable-next-line no-loop-func
    unmetDeps.forEach(function (node) {
      const index = node.dependsOn.indexOf(done.item.itemid);
      if (index !== -1) {
        node.dependsOn.splice(index, 1);
        if (node.dependsOn.length === 0) {
          metDeps.push(node);
        }
      }
    });
  }

  const left = unmetDeps.filter(node => node.dependsOn.length !== 0);
  if (left.length) {
    //https://en.wikipedia.org/wiki/DOT_(graph_description_language)#Directed_graphs
    const mapAsDot = `
    paste this into https://codepen.io/ledhund/pen/qBZMVwQ

    ${map2dot(left)}
    `;
    console.log(mapAsDot);
    throw new Error('Bad dependency graph');
  }
  return out;
}

function inDrawOrder(items) {
  const arr = items.slice();
  const deps = buildDependencies(arr);
  // filter deps to avoid any duplicate sprites without itemid
  const sortedDeps = sortDependencies(deps);
  const sortedItems = sortedDeps.map(itemid =>
    items.find(item => itemid === item.itemid)
  );

  // if temps have kids, keep them in regular draw order
  const tempsFirst = _.partition(sortedItems, function drawAtBottom(item) {
    return item.isTempItem && !item.items?.length;
  });
  return Array.prototype.concat.apply([], tempsFirst);
}

export default {
  inDrawOrder,
};
